import { CheckCircle2, HelpCircle } from "lucide-react";
import type { PageContent } from "@/content/site-pages";

interface Props {
  content: PageContent;
  faqTitle?: string;
}

/**
 * Renders intro paragraphs, body sections, and FAQ for static pages.
 * Pure presentation — no business logic, no province data.
 */
export function LongFormContent({ content, faqTitle = "คำถามที่พบบ่อย" }: Props) {
  return (
    <>
      {content.intro.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12 space-y-4">
            {content.intro.map((p, i) => (
              <p
                key={i}
                className="text-base leading-relaxed text-foreground/90"
              >
                {p}
              </p>
            ))}
          </div>
        </section>
      )}

      {content.sections.map((s, idx) => (
        <section
          key={idx}
          className={`border-b border-border ${idx % 2 === 0 ? "bg-muted/20" : ""}`}
        >
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">{s.h2}</h2>
            <div className="space-y-3">
              {s.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-base leading-relaxed text-foreground/90"
                >
                  {p}
                </p>
              ))}
            </div>
            {s.bullets && s.bullets.length > 0 && (
              <ul className="mt-5 space-y-2">
                {s.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}

      {content.faqs && content.faqs.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="mb-6 flex items-center gap-2 text-2xl font-bold md:text-3xl">
              <HelpCircle className="h-6 w-6 text-gold" />
              {faqTitle}
            </h2>
            <div className="space-y-4">
              {content.faqs.map((f, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="font-semibold text-foreground">{f.q}</div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
